// src/utils/firebase.js
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithCredential,
  reauthenticateWithCredential,
  signOut,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
  deleteUser,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

const firebaseConfig = {
  apiKey:            process.env.FIREBASE_API_KEY,
  authDomain:        process.env.FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.FIREBASE_PROJECT_ID,
  storageBucket:     process.env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.FIREBASE_APP_ID,
};

const app  = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Keep users logged in indefinitely across sessions
setPersistence(auth, browserLocalPersistence);

// ── Google Sign-In ────────────────────────────────────────────────────────────
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.setCustomParameters({ prompt: 'select_account' });

// ── Apple Sign-In ─────────────────────────────────────────────────────────────
const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

// signInWithPopup opens the OAuth flow in an actual browser popup, which only
// exists in a real browser tab. Inside Capacitor's native WKWebView there's
// no such popup — and Google actively detects & blocks OAuth sign-in run
// inside any embedded webview at all (disallowed_useragent), regardless of
// popup vs redirect. @capacitor-firebase/authentication routes the OS-level
// native Google/Apple SDKs instead (no webview involved in the credential
// exchange), then we bridge the resulting token into this same firebase/auth
// JS instance via signInWithCredential — so auth.currentUser / onAuthChange /
// everything else downstream never has to know which path was used.
// Capacitor.isNativePlatform() is false in a plain browser tab, so the
// original popup flow is untouched there.
const nativeGoogleCredential = async () => {
  const { credential } = await FirebaseAuthentication.signInWithGoogle();
  return GoogleAuthProvider.credential(credential?.idToken, credential?.accessToken);
};

const nativeAppleCredential = async () => {
  const { credential } = await FirebaseAuthentication.signInWithApple();
  return appleProvider.credential({
    idToken: credential?.idToken,
    rawNonce: credential?.nonce,
  });
};

export const signInWithGoogle = async () => {
  if (Capacitor.isNativePlatform()) {
    return signInWithCredential(auth, await nativeGoogleCredential());
  }
  return signInWithPopup(auth, googleProvider);
};

export const signInWithApple = async () => {
  if (Capacitor.isNativePlatform()) {
    return signInWithCredential(auth, await nativeAppleCredential());
  }
  return signInWithPopup(auth, appleProvider);
};

// ── Sign Out ──────────────────────────────────────────────────────────────────
// Also clears any locally-cached quiz/stats data so a different Google
// account signing in on the same device/browser never sees the previous
// account's leftovers (the offline localStorage fallback used to write to
// a single unscoped key shared by everyone).
export const signOutUser = async () => {
  clearLocalQuizCache();
  return signOut(auth);
};

// ── Auth State Observer ───────────────────────────────────────────────────────
export const onAuthChange = (callback) => onAuthStateChanged(auth, callback);

// ── Clear locally-cached quiz/stats data ────────────────────────────────────
// Shared by sign-out and delete-account so a stale account's data never
// leaks into whichever account uses this device/browser next.
const clearLocalQuizCache = () => {
  try {
    localStorage.removeItem('quiz_results');
    localStorage.removeItem('user_stats');
    Object.keys(localStorage)
      .filter((key) => key.startsWith('quiz_results_') || key.startsWith('user_stats_'))
      .forEach((key) => localStorage.removeItem(key));
  } catch (err) {
    console.warn('Failed to clear cached stats (non-fatal):', err);
  }
};

// ── Delete Account ───────────────────────────────────────────────────────────
// Deletes the signed-in Firebase user. Firebase requires a "recent" sign-in
// for this — if the session is stale it throws auth/requires-recent-login,
// so we transparently re-prompt the same provider's popup and retry once.
// Callers should delete the user's DB rows (via /api/delete-account) BEFORE
// calling this, since once the Firebase user is gone there's no user_id left
// to authenticate a follow-up server call with.
export const deleteUserAccount = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('No signed-in user to delete');
  }

  try {
    await deleteUser(currentUser);
  } catch (err) {
    if (err.code === 'auth/requires-recent-login') {
      const providerId = currentUser.providerData[0]?.providerId;
      if (Capacitor.isNativePlatform()) {
        const credential = providerId === 'apple.com'
          ? await nativeAppleCredential()
          : await nativeGoogleCredential();
        await reauthenticateWithCredential(currentUser, credential);
      } else {
        const provider = providerId === 'apple.com' ? appleProvider : googleProvider;
        await reauthenticateWithPopup(currentUser, provider);
      }
      await deleteUser(currentUser);
    } else {
      throw err;
    }
  }

  clearLocalQuizCache();
};
